import i18next from 'i18next';

type ApiErrorPayload = {
  type?: string;
  message?: string;
};

type AxiosLikeError = {
  isAxiosError?: boolean;
  message?: string;
  response?: {
    data?: unknown;
  };
};

const tryParseJson = <T>(value: string): T | null => {
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

export const parseApiJson = <T>(payload: unknown): T | null => {
  if (payload === null || payload === undefined) {
    return null;
  }

  if (typeof payload === 'string') {
    return tryParseJson<T>(payload);
  }

  if (typeof payload === 'object') {
    return payload as T;
  }

  return null;
};

export const parseApiCollection = <T>(payload: unknown): T[] => {
  const parsed = parseApiJson<T[]>(payload);
  return Array.isArray(parsed) ? parsed : [];
};

export const parseApiValue = <T>(payload: unknown, fallback: T): T => {
  const parsed = parseApiJson<T>(payload);
  return parsed ?? fallback;
};

export const parseNestedJson = <T>(payload: unknown, fallback: T): T => {
  if (payload === null || payload === undefined) {
    return fallback;
  }

  if (typeof payload === 'string') {
    const parsed = tryParseJson<T>(payload);
    return parsed ?? fallback;
  }

  return payload as T;
};

export const parseNestedCollection = <T>(payload: unknown): T[] => {
  return parseNestedJson<T[]>(payload, []);
};

const isAxiosLikeError = (error: unknown): error is AxiosLikeError => {
  return Boolean(
    error &&
      typeof error === 'object' &&
      ('isAxiosError' in error || 'response' in error || 'message' in error),
  );
};

const extractErrorMessageFromPayload = (payload: unknown, fallback?: string): string | null => {
  if (typeof payload === 'string') {
    const parsed = tryParseJson<ApiErrorPayload>(payload);

    if (parsed?.type === 'Unhandled error') {
      return fallback || i18next.t('somethingWentWrong');
    }

    if (parsed?.message) {
      return parsed.message;
    }

    return payload;
  }

  if (payload && typeof payload === 'object') {
    const parsed = payload as ApiErrorPayload;

    if (parsed.type === 'Unhandled error') {
      return fallback || i18next.t('somethingWentWrong');
    }

    if (parsed.message) {
      return parsed.message;
    }
  }

  return null;
};

export const getApiErrorMessage = (error: unknown, fallback?: string): string => {
  if (isAxiosLikeError(error)) {
    const responseMessage = extractErrorMessageFromPayload(error.response?.data, fallback);

    if (responseMessage) {
      return responseMessage;
    }

    if (error.message) {
      return error.message;
    }
  }

  const directMessage = extractErrorMessageFromPayload(error, fallback);
  if (directMessage) {
    return directMessage;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback || i18next.t('somethingWentWrong');
};
